import { compareAsc } from "date-fns";
import { createSelector } from "reselect";

import Role from "constants/role";
import { alphabeticalSortBySelector } from "helpers/sort";
import { IDeviceListItem } from "interfaces/entities/devices";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { IWithSessionState, getCurrentAgentId } from "store/features/session/selectors";
import { createRequestFetchingSelector } from "store/requests/selectors";

import { IEntityCollectionState } from "../reducer";

import { IAgent, IAgentFlags, WebpushPreferences } from "./interfaces";

export interface IWithAgentsState {
  entities: {
    agents: IEntityCollectionState<IAgent>;
  };
}

export interface IWithAgentsSessionState extends IWithAgentsState, IWithSessionState {}

interface ISelectAgentsAlphabeticallyOptions {
  activeOnly?: boolean;
  excludeViewers?: boolean;
  loggedInAgentOnTop?: boolean;
}

export const selectAgentsAlphabetically = createSelector(
  selectAgents,
  selectLoggedInAgent,
  (_: IWithAgentsState, options?: ISelectAgentsAlphabeticallyOptions) => options,
  (agents: IEntityCollectionState<IAgent>, loggedInAgent: IAgent, options?: ISelectAgentsAlphabeticallyOptions) => {
    let agentValues = Object.values(agents).sort(alphabeticalSortBySelector(agent => agent.name));

    if (options?.activeOnly !== undefined) {
      agentValues = agentValues.filter(agent => agent.status === "active");
    }

    if (options?.excludeViewers) {
      agentValues = agentValues.filter(agent => agent.permission !== Role.Viewer);
    }

    if (options?.loggedInAgentOnTop) {
      agentValues = agentValues.sort((a, b) => (a.id === loggedInAgent.id ? -1 : b.id === loggedInAgent.id ? 1 : 0));
    }

    return agentValues;
  }
);

function sortByCreationDate(a: IAgent, b: IAgent): number {
  return compareAsc(a.createdAt, b.createdAt);
}

export function selectAgents(state: IWithAgentsState): Record<string, IAgent> {
  return state.entities.agents;
}

export const getNonViewersCount = createSelector(
  selectAgentsAlphabetically,
  agents => agents.filter(agent => agent.permission !== Role.Viewer).length
);

export const selectAgentsCount = createSelector(selectAgentsAlphabetically, agents => agents.length);

export const selectOwner = createSelector(selectAgentsAlphabetically, agents =>
  agents.sort(sortByCreationDate).find((agent: IAgent) => agent.permission === Role.Owner)
);

export const selectOrganizationOwner = createSelector(selectAgentsAlphabetically, agents =>
  agents.find(agent => agent.organizationOwner)
);

export const selectAdmins = createSelector(selectAgentsAlphabetically, agents =>
  agents.sort(sortByCreationDate).filter((agent: IAgent) => agent.permission === Role.Owner)
);

export const selectActiveAgents = createSelector(selectAgentsAlphabetically, agents =>
  agents.filter(agent => agent.status === "active")
);

export const selectActiveAgentsWithoutViewers = createSelector(selectActiveAgents, agents =>
  agents.filter(agent => agent.permission !== Role.Viewer)
);

export function selectAgent(state: IWithAgentsState, id: string): IAgent {
  return state.entities?.agents?.[id] as IAgent;
}
export function selectAgentByAccountId(state: IWithAgentsState, accountId: string): IAgent {
  return state.entities?.agents?.[accountId] as IAgent;
}

export function selectAgentName(state: IWithAgentsState, login: string): string {
  const agent = selectAgent(state, login);
  return agent && agent.name;
}

export function selectAgentAvatar(state: IWithAgentsState, login: string): string | null {
  const agent = selectAgent(state, login);
  return agent ? agent.avatar : "";
}

export function selectLoggedInAgent(state: IWithAgentsSessionState): IAgent {
  return selectAgent(state, getCurrentAgentId(state) || "") || null;
}

export function selectLoggedInAgentLogin(state: IWithAgentsSessionState): string | undefined {
  return selectLoggedInAgent(state)?.email;
}

export function selectLoggedInAgentWebpushSubscriptions(state: IWithAgentsSessionState): IDeviceListItem[] | undefined {
  return selectLoggedInAgent(state)?.webpushSubscriptions;
}

export function selectLoggedInAgentPermission(state: IWithAgentsSessionState): Role | null {
  const agent = selectLoggedInAgent(state);
  return agent ? agent.permission : null;
}
export const selectIsAgentsCollectionFetching = createRequestFetchingSelector(["FETCH_COLLECTION_AGENT"]);

export const selectIsAgentDeviceSubscriptionFetching = createRequestFetchingSelector([
  "ENTITIES/AGENT/SUBSCRIBE_DEVICE",
]);

export function selectAgentFlagByName(state: IWithAgentsSessionState, flagName: keyof IAgentFlags): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags[flagName] : false);
}

export function selectLoggedInAgentCompletedWelcomeTutorialFlag(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "visitedWelcomeTutorial");
}

export function selectNextSubscriptionReminderDate(state: IWithAgentsSessionState): string {
  const agent = selectLoggedInAgent(state);
  return agent?.flags?.nextSubscriptionReminderDate ?? "";
}

export function selectHasSeenEnableNotificationBanner(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "hasSeenEnableNotificationBanner");
}

export function selectHasSeenEmailTemplatesPromoBanner(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "hasSeenEmailTemplatesPromoBanner");
}

export function selectHasSeenEmailTemplatesBuilderPromoBanner(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "hasSeenEmailTemplatesBuilderPromoBanner");
}

export function selectHasSeenEmailTemplatesSendTestTooltip(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "hasSeenEmailSendTestTooltip");
}

export function selectHasSeenSiloTooltip(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasSeenSiloTooltip : false);
}

export function selectHasSeenNoResultsInSiloTooltip(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasSeenNoResultsInSiloTooltip : false);
}

export function selectHasSeenCustomFieldsPromoBanner(state: IWithAgentsSessionState): boolean {
  return selectAgentFlagByName(state, "hasSeenCustomFieldsPromoBanner");
}

export function selectInviteAgentTooltipDisplayDate(state: IWithAgentsSessionState): string {
  const agent = selectLoggedInAgent(state);
  return agent?.flags?.inviteAgentTooltipDisplayDate ?? "";
}

export function selectSetDomainTooltipDisplayDate(state: IWithAgentsSessionState): string {
  const agent = selectLoggedInAgent(state);
  return agent?.flags?.setDomainTooltipDisplayDate ?? "";
}

export function selectHasSeenArchiveOnboardingBanner(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasSeenArchiveOnboardingBanner : false);
}

export function selectHasSeenOnboardingChecklist(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasSeenOnboardingChecklist : false);
}

export function selectHasVisitedAutomatedWorkflows(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasVisitedAutomatedWorkflows : false);
}

export function selectHasSeenNavigationInTicketTooltip(state: IWithAgentsSessionState): boolean {
  const agent = selectLoggedInAgent(state);
  return Boolean(agent ? agent.flags.hasSeenNavigationInTicketTooltip : false);
}

export function selectHasInvitedOtherAgent(state: IWithAgentsSessionState): boolean {
  const loggedInAgentId = getCurrentAgentId(state);
  const agents = selectAgents(state);

  return loggedInAgentId ? Object.values(agents).some(agent => agent.createdBy === loggedInAgentId) : false;
}

export function selectLoggedInAgentWebpushPreferences(state: IWithAgentsSessionState): WebpushPreferences {
  const agent = selectLoggedInAgent(state);
  return agent ? agent.settings.webpushNotificationsActive : WebpushPreferences.Always;
}

export function selectHasBusinessEmail(state: IWithAgentsSessionState): boolean {
  const agentEmail = selectLoggedInAgent(state)?.email;
  const privateDomains = ["gmail.com", "hotmail.com", "yahoo.com"];
  const isPrivateEmail = privateDomains.some(domain => agentEmail.endsWith(domain));
  return !isPrivateEmail;
}

export function selectIsViewer(state: IWithAgentsSessionState): boolean {
  return selectLoggedInAgent(state)?.permission === Role.Viewer;
}

export function selectIsAgent(state: IWithAgentsSessionState): boolean {
  return selectLoggedInAgent(state)?.permission === Role.Normal;
}

export function selectIsAdmin(state: IWithAgentsSessionState): boolean {
  return selectLoggedInAgent(state)?.permission === Role.Owner;
}

export function selectIsOrganizationOwner(state: IWithAgentsSessionState): boolean {
  return Boolean(selectLoggedInAgent(state)?.organizationOwner);
}

export function selectLastClosedGoogleVerification(state: IWithAgentsSessionState): string | null {
  return selectLoggedInAgent(state)?.flags?.lastClosedGoogleVerification ?? null;
}
